import React, { FunctionComponent, useEffect } from 'react';
import withStyles from 'react-jss';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Grid } from 'semantic-ui-react';
import { differenceInMonths, format } from 'date-fns';
import classnames from 'classnames';

import { ClassProp } from 'helpers/types';
import { DATE_FORMAT } from 'helpers/date';

import FormInput from 'components/FormComponents/FormInput';
import FormError from 'components/FormComponents/FormError';

import styles from './MonthlyBudgetsInput.styles';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  name: string;
  campaignStartDate: Date;
  campaignEndDate: Date;
  totalBudgetAmount: number;
};

type MonthlyBudget = {
  budgetStartDate: string;
  budgetEndDate: string;
  budgetAmount: string;
};

const MonthlyBudgetsInput: FunctionComponent<Props> = ({
  name,
  campaignStartDate,
  campaignEndDate,
  totalBudgetAmount,
  classes,
}) => {
  const { control, errors, register, watch, setValue } = useFormContext();

  const { fields: budgetInputs, append, remove } = useFieldArray<MonthlyBudget>(
    {
      control,
      name: 'budgets',
    },
  );

  const budgetAmts = watch('budgets') as MonthlyBudget[];

  const numberOfMonths =
    differenceInMonths(campaignEndDate, campaignStartDate) + 1;

  const monthlyBudgetAmount = Math.floor(totalBudgetAmount / numberOfMonths);

  useEffect(() => {
    if (budgetInputs.length === 0) {
      append({ [name]: '' });
    }
  }, []);

  useEffect(() => {
    if (numberOfMonths > budgetInputs.length) {
      append(Array(numberOfMonths - budgetInputs.length).fill({ [name]: '' }));
    }
    if (numberOfMonths < budgetInputs.length) {
      const elementsToRemove = [...Array(budgetInputs.length).keys()];
      elementsToRemove.splice(
        numberOfMonths,
        budgetInputs.length - numberOfMonths,
      );
      remove(elementsToRemove);
    }
    budgetInputs.forEach((field, index) => {
      setValue(`${name}[${index}].budgetAmount`, monthlyBudgetAmount);
    });
  }, [numberOfMonths, budgetInputs.length, monthlyBudgetAmount]);

  const checkBudgetNotExceeded = () => {
    const totalMonthlyBudget = budgetAmts
      .map((budget) => budget.budgetAmount)
      .reduce((totalBudget, item) => totalBudget + parseFloat(item ?? '0'), 0);
    if (totalMonthlyBudget > totalBudgetAmount) {
      return false;
    }
    return true;
  };

  return (
    <>
      <Grid.Row>
        <Grid.Column>
          <h3 className={classes.monthlyBudgetsTitle}> Monthly Budgets </h3>
        </Grid.Column>
      </Grid.Row>
      <Grid className={classes.monthlyBudgets}>
        {budgetInputs.map((item, index) => (
          <Grid.Row className={classes.monthlyBudgetsRow} key={item.id}>
            <Grid.Column width={5}>
              <div className={classnames(classes.container)}>
                <span className={classes.fieldLabel}>Start Date</span>
                <input
                  className={classnames(classes.fieldInput, classes.disabled)}
                  type="text"
                  readOnly
                  name={`${name}[${index}].budgetStartDate`}
                  ref={register()}
                  value={format(
                    index === 0
                      ? campaignStartDate
                      : new Date(
                          campaignStartDate.getFullYear(),
                          campaignStartDate.getMonth() + index,
                        ),
                    DATE_FORMAT,
                  )}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={5}>
              <div className={classnames(classes.container)}>
                <span className={classes.fieldLabel}>End Date</span>
                <input
                  className={classnames(classes.fieldInput, classes.disabled)}
                  type="text"
                  readOnly
                  name={`${name}[${index}].budgetEndDate`}
                  ref={register()}
                  value={format(
                    index === budgetInputs.length - 1
                      ? campaignEndDate
                      : new Date(
                          campaignStartDate.getFullYear(),
                          campaignStartDate.getMonth() + index + 1,
                          0,
                        ),
                    DATE_FORMAT,
                  )}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={5}>
              <FormInput
                name={`budgets[${index}].budgetAmount`}
                type="text"
                placeholder={monthlyBudgetAmount.toString()}
                label="Monthly Budget Amount"
                register={register({
                  required: 'Budget amount is required.',
                  validate: {
                    oneOf: () =>
                      checkBudgetNotExceeded() ||
                      'Total Monthly Budget cannot exceed Insertion Order Amount',
                  },
                })}
              />
            </Grid.Column>
          </Grid.Row>
        ))}
        {errors && <FormError name={name} errors={errors} />}
      </Grid>
    </>
  );
};
export default withStyles(styles)(MonthlyBudgetsInput);
